import React, { useState } from 'react';
import { TagIcon, PlusIcon, CalendarIcon, EditIcon, TrashIcon, CheckCircleIcon, ClockIcon, EyeIcon, MousePointerClickIcon, MegaphoneIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useNavigate } from 'react-router-dom';
// Mock data updated structure
const promotions = [{
  id: 1,
  bookTitle: 'The Midnight Garden',
  author: 'Sarah Chen',
  discountType: 'percentage' as const,
  discountValue: 20,
  regularPrice: null,
  dealType: 'platform' as const,
  platformName: 'Bookbub',
  status: 'active' as const,
  startDate: '2026-02-01',
  endDate: '2026-02-28',
  tagline: "Limited time — grab it before it's gone!",
  views: 1240,
  clicks: 145
}, {
  id: 2,
  bookTitle: 'Echoes of Tomorrow',
  author: 'Marcus Webb',
  discountType: 'fixed' as const,
  discountValue: 2.99,
  regularPrice: 5.99,
  dealType: 'self' as const,
  platformName: null,
  status: 'scheduled' as const,
  startDate: '2026-03-01',
  endDate: '2026-03-07',
  tagline: 'Spring sale — one week only!',
  views: 0,
  clicks: 0
}, {
  id: 3,
  bookTitle: 'Shadows & Starlight',
  author: 'Elena Vasquez',
  discountType: 'percentage' as const,
  discountValue: 50,
  regularPrice: null,
  dealType: 'platform' as const,
  platformName: 'StoryOrigin',
  status: 'expired' as const,
  startDate: '2026-01-01',
  endDate: '2026-01-15',
  tagline: null,
  views: 3200,
  clicks: 89
}];
const Promotions = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const filteredPromotions = promotions.filter(promo => filterStatus === 'all' || promo.status === filterStatus);
  // Calculate stats
  const activeCount = promotions.filter(p => p.status === 'active').length;
  const scheduledCount = promotions.filter(p => p.status === 'scheduled').length;
  const totalClicks = promotions.reduce((acc, curr) => acc + curr.clicks, 0);
  // Helper for date formatting
  const formatDateRange = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric'
    };
    const yearOptions: Intl.DateTimeFormatOptions = {
      ...options,
      year: 'numeric'
    };
    // If same year, only show year at end
    if (s.getFullYear() === e.getFullYear()) {
      return `${s.toLocaleDateString('en-US', options)} – ${e.toLocaleDateString('en-US', yearOptions)}`;
    }
    return `${s.toLocaleDateString('en-US', yearOptions)} – ${e.toLocaleDateString('en-US', yearOptions)}`;
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-12885" data-mp="1xjsy2yy6y0qd">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12886" data-mp="25329syu66bil">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8" data-id="element-12887" data-mp="2a1vkyklzhqfl">
          <div className="flex-1 min-w-0" data-id="element-12888" data-mp="29zsmdc6rmoz9">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-12889" data-mp="2es29ylkercyd">
              Promotions
            </h1>
            <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-12890" data-mp="2jryf4o4udafi">
              Manage your active deals and sale campaigns
            </p>
          </div>
          <div className="mt-4 md:mt-0" data-id="element-12891" data-mp="1m69d0slsmsji">
            <button onClick={() => navigate('/vendors/manage?type=sale&returnTo=/promotions')} className={`font-medium px-4 py-2 rounded-lg flex items-center text-white transition-colors ${rt.bgDark} hover:opacity-90`} data-id="element-12892" data-mp="1r27x44039oe4">
              <PlusIcon className="h-5 w-5 mr-2" data-id="element-12893" data-mp="37hsqt2dfcsy6" />
              Create Promotion
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-12894" data-mp="3bkxomrmk86gh">
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-12895" data-mp="3a2gx8ofantv3">
            <div className="flex items-center justify-between" data-id="element-12896" data-mp="353e2pxwxx61d">
              <div data-id="element-12897" data-mp="354ytrdk1q6uh">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-12898" data-mp="3k0ej559olekj">
                  Active Promotions
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-12899" data-mp="3ezqs4jlgcxqb">
                  {activeCount}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'}`} data-id="element-12900" data-mp="3sy71kiap92c5">
                <CheckCircleIcon className="h-6 w-6" data-id="element-12901" data-mp="6jnoqq1dhv7s" />
              </div>
            </div>
          </div>
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-12902" data-mp="1mdyx9a2nhjf">
            <div className="flex items-center justify-between" data-id="element-12903" data-mp="fmos4iyut87r">
              <div data-id="element-12904" data-mp="bk1xybepnb7e">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-12905" data-mp="bklblfxbvdfm">
                  Scheduled
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-12906" data-mp="u9f4qgi12n8n">
                  {scheduledCount}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-12907" data-mp="z52pmjph8u8c">
                <ClockIcon className="h-6 w-6" data-id="element-12908" data-mp="q5fntwsu5mer" />
              </div>
            </div>
          </div>
          <div className={`p-5 rounded-xl shadow-sm border ${dk.card}`} data-id="element-12909" data-mp="bh2u76h80fnf">
            <div className="flex items-center justify-between" data-id="element-12910" data-mp="g8ujldon4fbz">
              <div data-id="element-12911" data-mp="gafc6xre9o77">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-12912" data-mp="2hpsizzc8vd9">
                  Total Clicks
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-12913" data-mp="6jt5xu5kia16">
                  {totalClicks}
                </p>
              </div>
              <div className={`p-3 rounded-full ${darkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`} data-id="element-12914" data-mp="3oqqo670kd9v4">
                <MousePointerClickIcon className="h-6 w-6" data-id="element-12915" data-mp="1z825jkrj9i70" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide" data-id="element-12916" data-mp="1qep5zzyyriby">
          {['all', 'active', 'scheduled', 'expired'].map(status => <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap capitalize ${filterStatus === status ? `${rt.bgDark} text-white` : `border ${dk.border} ${dk.surface} ${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-12917" data-mp="1lhfaq3ow8cg7">
              {status}
            </button>)}
        </div>

        {/* Promotions Grid */}
        {filteredPromotions.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-12918" data-mp="xo6v4cs00cnz">
            {filteredPromotions.map(promo => <div key={promo.id} className={`rounded-xl shadow-sm border overflow-hidden flex flex-col ${dk.card} ${promo.status === 'active' ? 'border-l-4 border-l-green-500' : promo.status === 'scheduled' ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-gray-400'}`} data-id="element-12919" data-mp="inrxienp4bsq">
                <div className="p-5 flex-1 flex flex-col" data-id="element-12920" data-mp="xd7j7g8ddr1k">
                  {/* Top Row: Status & Deal Type */}
                  <div className="flex justify-between items-start mb-4" data-id="element-12921" data-mp="11d7yf33am69o">
                    <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium capitalize ${promo.status === 'active' ? darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800' : promo.status === 'scheduled' ? darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-800'}`} data-id="element-12922" data-mp="16c1b4tar9t8s">
                      {promo.status}
                    </span>

                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${promo.dealType === 'platform' ? darkMode ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-12923" data-mp="1nsyidlc5zfk8">
                      {promo.dealType === 'platform' ? <>
                          <MegaphoneIcon className="w-3 h-3 mr-1" data-id="element-12924" data-mp="14xrpcpbwlx88" />
                          {promo.platformName}
                        </> : <>
                          <TagIcon className="w-3 h-3 mr-1" data-id="element-12925" data-mp="1du92tz7qq775" />
                          Self-run
                        </>}
                    </span>
                  </div>

                  {/* Book Info */}
                  <div className="mb-4" data-id="element-12926" data-mp="2laggtn6czqy1">
                    <h3 className={`font-bold text-lg leading-tight mb-1 ${dk.textHeading}`} data-id="element-12927" data-mp="2l9dp5d17pkh2">
                      {promo.bookTitle}
                    </h3>
                    <p className={`text-sm ${dk.textMuted}`} data-id="element-12928" data-mp="2xu3z10oaj3m6">
                      by {promo.author}
                    </p>
                  </div>

                  {/* Discount Badge */}
                  <div className="mb-5" data-id="element-12929" data-mp="2nv4sbnn35i1t">
                    <div className={`inline-block px-3 py-1.5 rounded-lg font-bold text-sm ${darkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'}`} data-id="element-12930" data-mp="3bjxnvizspnvw">
                      {promo.discountType === 'percentage' ? <span className="flex items-center" data-id="element-12931" data-mp="31s1n206brjnz">
                          <span className="text-lg mr-1" data-id="element-12932" data-mp="36rwfw99nujbo">
                            {promo.discountValue}%
                          </span>{' '}
                          OFF
                        </span> : <span className="flex items-baseline" data-id="element-12933" data-mp="3674s98y57tl">
                          <span className="text-lg mr-1" data-id="element-12934" data-mp="2mhvjai569x22">
                            ${promo.discountValue}
                          </span>
                          <span className="text-xs font-normal opacity-75 line-through" data-id="element-12935" data-mp="2qft7pir4cf4e">
                            ${promo.regularPrice}
                          </span>
                        </span>}
                    </div>
                  </div>

                  {/* Date & Tagline */}
                  <div className="mt-auto space-y-2" data-id="element-12936" data-mp="27j1hepd5szqu">
                    <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-12937" data-mp="27kuscgut264n">
                      <CalendarIcon className={`w-4 h-4 mr-2 ${dk.textMuted}`} data-id="element-12938" data-mp="1tq250w8oblni" />
                      {formatDateRange(promo.startDate, promo.endDate)}
                    </div>

                    {promo.tagline && <p className={`text-xs italic truncate ${dk.textMuted}`} data-id="element-12939" data-mp="1osadi3bxy1ju">
                        "{promo.tagline}"
                      </p>}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className={`px-5 py-3 border-t flex items-center justify-between ${dk.divider} ${dk.surfaceMuted}`} data-id="element-12940" data-mp="1f5pjkwh26yow">
                  <div className="flex items-center gap-4" data-id="element-12941" data-mp="1a866w100etz2">
                    <div className={`flex items-center text-xs ${dk.textMuted}`} title="Views" data-id="element-12942" data-mp="1o4t4m5zsdo61">
                      <EyeIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-12943" data-mp="1xvo1e4ans65q" />
                      {promo.views.toLocaleString()}
                    </div>
                    <div className={`flex items-center text-xs ${dk.textMuted}`} title="Clicks" data-id="element-12944" data-mp="1sxmbp0iq98la">
                      <MousePointerClickIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-12945" data-mp="22v0o8p1j5d8b" />
                      {promo.clicks.toLocaleString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-2" data-id="element-12946" data-mp="29w4988p7iy1x">
                    <button onClick={() => navigate(`/vendors/manage?type=sale&id=${promo.id}`)} className={`p-1.5 rounded-md transition-colors hover:bg-gray-200 dark:hover:bg-gray-700 ${dk.textSecondary}`} title="Edit Promotion" data-id="element-12947" data-mp="2extltss2vjs4">
                      <EditIcon className="w-4 h-4" data-id="element-12948" data-mp="2xn06hlcfmlgk" />
                    </button>
                    <button className="p-1.5 rounded-md transition-colors hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" title="Delete Promotion" data-id="element-12949" data-mp="32mmijrgixvns">
                      <TrashIcon className="w-4 h-4" data-id="element-12950" data-mp="1894xcb3ldavx" />
                    </button>
                  </div>
                </div>
              </div>)}
          </div> : <div className={`text-center py-20 rounded-xl border-2 border-dashed ${dk.border}`} data-id="element-12951" data-mp="15nx5uzgrz5gr">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-12952" data-mp="10x70hm8cn2m2">
              <TagIcon className={`w-8 h-8 ${dk.textMuted}`} data-id="element-12953" data-mp="qzj1gk3pn8y3" />
            </div>
            <h3 className={`text-lg font-medium mb-1 ${dk.textHeading}`} data-id="element-12954" data-mp="qxg0x64xlxf4">
              No promotions found
            </h3>
            <p className={`text-sm mb-6 ${dk.textSecondary}`} data-id="element-12955" data-mp="1jo7x43lzym2g">
              {filterStatus === 'all' ? 'Create your first sale or deal to get started.' : `No ${filterStatus} promotions found.`}
            </p>
            <button onClick={() => navigate('/vendors/manage?type=sale')} className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors ${rt.bgDark} hover:opacity-90`} data-id="element-12956" data-mp="19p8up2rtrmnw">
              <PlusIcon className="w-5 h-5 mr-2" data-id="element-12957" data-mp="1n52nq4u4e4tw" />
              Create Promotion
            </button>
          </div>}
      </div>
    </div>;
};
export default Promotions;